<template>
  <div :class="bem.b()">
    <div :class="bemContainer.b()">
      <a-input-group compact>
        <!-- 左侧select -->
        <a-select
          v-model:value="selectType"
          class="select-group-left-select-container"
          :style="{ minWidth: leftSelectWidth }"
          @change="handleSelectChange"
        >
          <a-select-option
            v-for="item in newList"
            :key="item.name"
            :value="item.value"
            :item="item"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
        <!-- 右侧组合表单 -->
        <!-- input输入框 -->
        <template
          v-if="
            !selectItem?.showFormType || selectItem?.showFormType === 'input'
          "
        >
          <a-input
            v-model:value="value"
            :class="bem.e('right-input')"
            :placeholder="selectItem?.placeholder"
            allow-clear
            @press-enter="handleChange"
          >
            <template #suffix>
              <ll-icon :icon-name="`icon-search`" @click="handleChange" />
            </template>
          </a-input>
        </template>
        <!-- select下拉框 -->
        <template v-else-if="selectItem?.showFormType === 'select'">
          <a-select
            v-bind="selectItem"
            v-model:value="value"
            :class="bem.e('right-select')"
            popup-class-name="select-group-right-select"
            @change="handleChange"
          >
            <a-select-option
              v-for="item in selectItem.list"
              :key="item.value"
              :value="item.value"
            >
              {{ item.name }}
            </a-select-option>
          </a-select>
        </template>
        <!-- 日期选择 -->
        <template v-else-if="selectItem?.showFormType === 'range-picker'">
          <a-range-picker
            v-model:value="value"
            :class="bem.e('right-range-picker')"
            @change="handleChange"
          />
        </template>
        <!-- 树选择 -->
        <template v-else-if="selectItem?.showFormType === 'treeSelect'">
          <a-tree-select
            v-model:value="value"
            :class="bem.e('right-tree-select')"
            :field-names="selectItem.fieldNames"
            :placeholder="selectItem?.placeholder"
            :tree-data="selectItem.list"
            @select="handleChange"
          ></a-tree-select>
        </template>
        <template v-else>
          <slot name="extra" v-bind="selectItem" />
        </template>
      </a-input-group>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { has } from 'lodash-es'
import { computed, ref, watch } from 'vue'

import { createNamespace, hasValue } from '@ll-plus/utils'
import {
  LEFT_SELECT_MIN_WIDTH,
  selectGroupEmits,
  selectGroupProps,
} from './select-group'

import type { SelectGroupData } from './select-group'

// 1.defineOptions
defineOptions({ name: 'LlSelectGroup' })

// 1.props/emits
const props = defineProps(selectGroupProps)
const emits = defineEmits(selectGroupEmits)

// 3.ref/const
const selectType = ref() // selectinput组合select的值
const value = ref<any>('') // 左边的value
// const selectItem = ref<SelectGroupData>() // 选中表单的类型
const newList = ref<SelectGroupData[]>([]) // 处理后的数据，比如select的类型前面加个全部
const bem = createNamespace('select-group')
const bemContainer = createNamespace('select-group-container')

const selectItem = computed(() => {
  return newList.value.find((e) => e.value === selectType.value)
})

// 动态计算左边文字最长的宽度
const leftSelectWidth = computed(() => {
  const names = newList.value.map((item) => item.name.length)
  const maxNameLength = Math.max(...names)
  if (maxNameLength <= 3) {
    return LEFT_SELECT_MIN_WIDTH + 'px'
  }
  const width = LEFT_SELECT_MIN_WIDTH + (maxNameLength - 3) * 14
  return width + 'px'
})

// 4.methods
// 第一个select的change
const handleSelectChange = (_: string, options: any) => {
  const item = options.item
  const formType = item.showFormType
  // 如果
  if (!formType || formType === 'input' || formType === 'treeSelect') {
    value.value = ''
  } else if (formType === 'range-picker') {
    value.value = [undefined, undefined]
  } else if (formType === 'select') {
    value.value = item.noFirstAddAll ? undefined : -1
  }
  // selectItem.value = options.item
  selectType.value = _

  emits('onSelectChange', _, item)
}
// 整合处理change
const handleChange = () => {
  // 过滤-1的下拉全部
  emits(
    'change',
    selectItem.value?.value as string,
    value.value === -1 ? null : value.value
  )
}
const setSelectGroup = (selectValue: string, currentValue: any) => {
  selectType.value = selectValue
  value.value = currentValue
}

// 5.watch
// 已第一个为标准
watch(
  () => props.data as SelectGroupData[],
  (newData) => {
    let list: SelectGroupData[] = []
    if (newData && newData.length) {
      list = newData.map((item) => {
        const newItem = { ...item }
        if (newItem.showFormType === 'select') {
          if (has(newItem, 'options') && !newItem.noFirstAddAll) {
            newItem.options = [
              {
                label: '全部',
                value: -1,
              },
              ...newItem.options!,
            ]
          } else if (has(newItem, 'list') && !newItem.noFirstAddAll) {
            newItem.list = [
              {
                name: '全部',
                value: -1,
              },
              ...newItem.list!,
            ]
          }
        }
        return newItem
      })

      newList.value = list
      if (newList.value.length && !props.selectValue) {
        selectType.value = newList.value[0].value
      }
    }
  },
  {
    deep: true,
    immediate: true,
  }
)

watch(
  () => [props.value, props.selectValue] as [any, string],
  ([currentValue, selectValue]) => {
    selectType.value = selectValue
      ? selectValue
      : newList.value.length
      ? newList.value[0].value
      : ''
    if (selectType.value) {
      const find = newList.value.find((e) => e.value === selectType.value)
      // selectItem.value = find
      if (find && find.showFormType === 'select' && !find.noFirstAddAll) {
        value.value = !hasValue(currentValue) ? -1 : currentValue
      } else {
        value.value = currentValue
      }
    }
  },
  {
    immediate: true,
  }
)

defineExpose({ setSelectGroup, selectType, value })
</script>
